import React, { useMemo, useState } from "react";

import {
  X,
  Wallet,
  Landmark,
  Smartphone,
  Receipt,
  BadgeDollarSign,
  MessageSquareText,
  ReceiptText,
  Hash

} from "lucide-react";

import { toast } from "react-toastify";

import "./styles/NewPaymentModal.css";

const API_BASE =
  "https://receivablesbackend.onrender.com/api";

const paymentModes = [
  {
    key: "Cash",
    icon: Wallet,
  },
  {
    key: "MMG",
    icon: Smartphone,
  },
  {
    key: "Bank",
    icon: Landmark,
  },
{
  key: "Account Credit",
  icon: BadgeDollarSign,
},
];

const NewPaymentModalModern = ({
  isOpen,
  onClose,
  client,
  invoices,
  payments,
  onSuccess,
}) => {

  const [loading, setLoading] = useState(false);

  const [mode, setMode] = useState("Cash");


const [referenceType, setReferenceType] =
    useState("Cheque");

const [referenceNumber, setReferenceNumber] =
    useState("");

const [generalRemarks, setGeneralRemarks] =
    useState("");


  const [selectedInvoices, setSelectedInvoices] =
    useState([]);

const availableCredit =
  Number(
    client?.credit_balance || 0
  );

  // =====================================================
  // OUTSTANDING INVOICES
  // =====================================================

  const outstandingInvoices =
  (invoices || [])
    .map((inv) => {

      const relatedPayments = payments.filter(
        (p) =>
          p.invoice_number ===
          inv.invoice_number
      );

      const totalPaid =
        relatedPayments.reduce(
          (sum, p) =>
            sum + Number(p.amount || 0),
          0
        );

      const outstanding =
        Number(inv.amount || 0) -
        totalPaid;

      return {
        ...inv,
        outstanding,
      };
    })

    .filter((inv) => inv.outstanding > 0);

  // =====================================================
  // SELECT INVOICE
  // =====================================================

  const toggleInvoice = (invoice) => {

    const exists = selectedInvoices.find(
      (i) => i.id === invoice.id
    );

    if (exists) {

      setSelectedInvoices((prev) =>
        prev.filter(
          (i) => i.id !== invoice.id
        )
      );

    } else {

      setSelectedInvoices((prev) => [
        ...prev,
        {
          id: invoice.id,
          invoice_number:
            invoice.invoice_number,
          outstanding:
            invoice.outstanding,
          pay_amount:
            invoice.outstanding,
        },
      ]);

    }
  };

  // =====================================================
  // TOTAL
  // =====================================================

  const totalPayment = useMemo(() => {

    return selectedInvoices.reduce(
      (sum, inv) =>
        sum +
        Number(inv.pay_amount || 0),
      0
    );

  }, [selectedInvoices]);

// =====================================================
// SUBMIT
// =====================================================

const handleSubmit = async () => {

  if (selectedInvoices.length === 0) {

    toast.error(
      "Select at least one invoice."
    );

    return;

  }

  if (
    mode === "Account Credit" &&
    totalPayment > availableCredit
  ) {

    toast.error(
      "Not enough account credit available."
    );

    return;

  }

  /* ==========================
     VALIDATE REFERENCES
  ========================== */

  if (
    mode === "MMG" &&
    !referenceNumber.trim()
  ) {

    toast.error(
      "Please enter the MMG reference number."
    );

    return;

  }

  if (
    mode === "Bank" &&
    !referenceNumber.trim()
  ) {

    toast.error(
      `Please enter the ${referenceType} number.`
    );

    return;

  }

  /* ==========================
     BUILD REMARKS
  ========================== */

  let remarks = "";

  if (mode === "MMG") {

    remarks =
      `MMG Reference: ${referenceNumber}`;

  }

  if (mode === "Bank") {

if (referenceType === "Cheque") {

  remarks =
    `Cheque Number: ${referenceNumber}`;

} else {

  remarks =
    `EFT Reference: ${referenceNumber}`;

}

  }

  if (
    generalRemarks.trim()
  ) {

    remarks +=
      remarks
        ? `\n\n${generalRemarks}`
        : generalRemarks;

  }

  try {

    setLoading(true);

    const payload =
      selectedInvoices.map((inv) => ({

        pos_customer_id:
          Number(client.id),

        invoice_id:
          inv.id,

        invoice_number:
          inv.invoice_number,

        amount:
          Number(inv.pay_amount),

        mode,

        used_credit:
          mode ===
          "Account Credit",

        remarks,

        payment_date:
          new Date()
            .toISOString()
            .split("T")[0],

      }));

    const res =
      await fetch(
        `${API_BASE}/payments`,
        {

          method: "POST",

          headers: {
            "Content-Type":
              "application/json",
          },

          body:
            JSON.stringify(
              payload
            ),

        }
      );

    if (!res.ok) {

      throw new Error();

    }

    toast.success(
      "Payment applied successfully."
    );

    onSuccess();

    onClose();

  } catch (err) {

    toast.error(
      "Failed to apply payment."
    );

  } finally {

    setLoading(false);

  }

};

// Don't render when closed
if (!isOpen) {
  return null;
}



return (
  <div className="rcs-payment-overlay">

    <div className="rcs-payment-modal-box">

      {/* HEADER */}

      <div className="rcs-payment-header">

        <div className="rcs-payment-header-left">
          <h2 className="rcs-payment-title">
            Record Payment
          </h2>

          <p className="rcs-payment-customer">
            {client?.name || "Select Customer"}
          </p>
        </div>

        <button
          className="rcs-payment-close-btn"
          onClick={onClose}
        >
          <X size={18} />
        </button>

      </div>

      {/* PAYMENT MODES */}

      <div className="rcs-payment-mode-grid">

        {paymentModes.map((m) => {

          const Icon = m.icon;

          return (
            <button
              key={m.key}
              className={`rcs-payment-mode-card ${
                mode === m.key
                  ? "rcs-payment-mode-active"
                  : ""
              }`}
              onClick={() =>
                setMode(m.key)
              }
            >

              <Icon size={18} />

              <span>{m.key}</span>

            </button>
          );
        })}

      </div>

      {/* CREDIT */}

      {availableCredit > 0 && (

        <div className="rcs-payment-credit-box">

          <div>
            <span className="rcs-payment-credit-label">
              Available Account Credit
            </span>

            <strong className="rcs-payment-credit-value">
              $
              {availableCredit.toLocaleString(
                "en-US",
                {
                  minimumFractionDigits: 2,
                }
              )}
            </strong>
          </div>

          {mode === "Account Credit" && (
            <div className="rcs-payment-credit-badge">
              USING CREDIT
            </div>
          )}

        </div>

      )}

      {/* INVOICES */}

      <div className="rcs-payment-invoice-scroll">

        {outstandingInvoices.length > 0 ? (

          outstandingInvoices.map((inv) => {

            const selected =
              selectedInvoices.find(
                (i) => i.id === inv.id
              );

            return (
              <div
                key={inv.id}
                className={`rcs-payment-invoice-card ${
                  selected
                    ? "rcs-payment-invoice-selected"
                    : ""
                }`}
                onClick={() =>
                  toggleInvoice(inv)
                }
              >

                <div className="rcs-payment-invoice-top">

                  <div>

                    <span className="rcs-payment-small-label">
                      Invoice
                    </span>

                    <h4 className="rcs-payment-invoice-number">
                      {inv.invoice_number}
                    </h4>

                  </div>

                  <Receipt size={18} />

                </div>

                <div className="rcs-payment-invoice-bottom">

                  <div>

                    <span className="rcs-payment-small-label">
                      Outstanding
                    </span>

                    <strong className="rcs-payment-amount">
                      $
                      {Number(
                        inv.outstanding
                      ).toLocaleString(
                        "en-US",
                        {
                          minimumFractionDigits: 2,
                        }
                      )}
                    </strong>

                  </div>

                  {selected && (

                    <input
                      type="number"
                      className="rcs-payment-input"
                      value={
                        selected.pay_amount
                      }
                      max={
                        inv.outstanding
                      }
                      onClick={(e) =>
                        e.stopPropagation()
                      }
                      onChange={(e) => {

                        const value =
                          Number(
                            e.target.value
                          );

                        setSelectedInvoices(
                          (prev) =>
                            prev.map((p) =>
                              p.id === inv.id
                                ? {
                                    ...p,
                                    pay_amount:
                                      value,
                                  }
                                : p
                            )
                        );
                      }}
                    />

                  )}

                </div>

              </div>
            );
          })

        ) : (

          <div className="rcs-payment-empty-state">

            <BadgeDollarSign size={38} />

            <h3>
              No Outstanding Invoices
            </h3>

          </div>

        )}

      </div>

      {/* SUMMARY */}

      <div className="rcs-payment-summary-box">

        <span className="rcs-payment-summary-label">
          Total Payment
        </span>

        {mode === "Account Credit" && (

          <div className="rcs-payment-credit-summary">

            <p>
              Current Credit:

              <span>
                $
                {availableCredit.toLocaleString(
                  "en-US",
                  {
                    minimumFractionDigits: 2,
                  }
                )}
              </span>
            </p>

            <p>
              Remaining:

              <span
                className={
                  availableCredit -
                    totalPayment <
                  0
                    ? "rcs-payment-red"
                    : "rcs-payment-green"
                }
              >
                $
                {(
                  availableCredit -
                  totalPayment
                ).toLocaleString(
                  "en-US",
                  {
                    minimumFractionDigits: 2,
                  }
                )}
              </span>
            </p>

          </div>

        )}




<div className="rcs-payment-details-card">

    <div className="rcs-payment-details-header">

        <ReceiptText size={18} />

        <h3>
            Payment Details
        </h3>

    </div>

    {mode === "MMG" && (

        <div className="rcs-payment-field">

            <label>

                <Hash size={16} />

                MMG Reference Number

            </label>

            <input

                type="text"

                value={referenceNumber}

                onChange={(e)=>
                    setReferenceNumber(
                        e.target.value
                    )
                }

                placeholder="Enter MMG reference"

            />

        </div>

    )}

    {mode === "Bank" && (

        <>

            <div className="rcs-reference-options">

                <button

                    type="button"

                    className={
                        referenceType === "Cheque"
                        ? "reference-active"
                        : ""
                    }

                    onClick={()=>
                        setReferenceType(
                            "Cheque"
                        )
                    }

                >

                    Cheque

                </button>

                <button

                    type="button"

                    className={
                        referenceType === "EFT"
                        ? "reference-active"
                        : ""
                    }

                    onClick={()=>
                        setReferenceType(
                            "EFT"
                        )
                    }

                >

                    EFT

                </button>

            </div>

            <div className="rcs-payment-field">

                <label>

                    <Hash size={16} />

                    {referenceType}
                    {" "}
                    Number

                </label>

                <input

                    type="text"

                    value={referenceNumber}

                    onChange={(e)=>
                        setReferenceNumber(
                            e.target.value
                        )
                    }

                    placeholder={`Enter ${referenceType} number`}

                />

            </div>

        </>

    )}

    <div className="rcs-payment-field">

        <label>

            <MessageSquareText size={16} />

            General Remarks

        </label>

        <textarea

            rows={3}

            value={generalRemarks}

            onChange={(e)=>
                setGeneralRemarks(
                    e.target.value
                )
            }

            placeholder="Optional notes..."

        />

    </div>

</div>



        <strong className="rcs-payment-total-value">
          $
          {totalPayment.toLocaleString(
            "en-US",
            {
              minimumFractionDigits: 2,
            }
          )}
        </strong>

        <button
          className="rcs-payment-submit-btn"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading
            ? "Applying..."
            : "Apply Payment"}
        </button>

      </div>

    </div>

  </div>
);
};

export default NewPaymentModalModern;